/**
 * Groups a plan's flat GraphOp[] into checklist rows (PlanStep) and groups
 * those rows into checkpoint phases (PlanPhase), for the step-by-step
 * animated build in usePlanReveal.ts. Pure and stateless -- given the same
 * ops and the OpStepResult[] captured by applyGraphOps({recordSteps: true}),
 * always produces the same steps/phases.
 */

import type { AddConditionOp, GraphOp } from './graphOps';
import type { OpStepResult } from './applyGraphOps';

/** Only split a branch-contents phase further once it exceeds this many rows. */
export const MAX_PHASE_STEPS = 6;

/** One checklist row: a single add_node (+ its immediately-trailing
 * configure_node/rename_node ops) or a single add_branch (+ its
 * immediately-trailing add_condition ops), folded into one visible step.
 * Any other op (delete_node, or a configure_node/rename_node/add_condition
 * that doesn't immediately follow its own add_node/add_branch) is its own
 * single-op step. */
export interface PlanStep {
  /** Index into the plan's ops[] of this step's primary op. */
  primaryOpIndex: number;
  /** Every op index folded into this step, ascending, primaryOpIndex included. */
  opIndices: number[];
  /** The node this step is about -- the added node, or the new branch's
   * conditional node -- resolved from OpStepResult.primaryNodeId. Undefined
   * for a delete_node step (the node no longer exists once it runs). */
  nodeId: string | undefined;
  addedNodeIds: string[];
  modifiedNodeIds: string[];
  removedNodeIds: string[];
}

function changedIds(opIndices: number[], opSteps: OpStepResult[]) {
  const collect = (
    key: 'addedNodeIds' | 'modifiedNodeIds' | 'removedNodeIds'
  ) => [...new Set(opIndices.flatMap((index) => opSteps[index]?.[key] ?? []))];
  return {
    addedNodeIds: collect('addedNodeIds'),
    modifiedNodeIds: collect('modifiedNodeIds'),
    removedNodeIds: collect('removedNodeIds'),
  };
}

export interface PlanPhase {
  /** Short label for this phase's checkpoint chip, e.g. "Core skeleton". */
  label: string;
  steps: PlanStep[];
}

function refersToNode(op: GraphOp, ref: string): boolean {
  return (
    (op.op === 'configure_node' || op.op === 'rename_node') && op.node === ref
  );
}

/** Folds a flat ops[] into checklist rows. `opSteps` is the same-length
 * array applyGraphOps({recordSteps: true}) returns for a successful plan --
 * opSteps[i].opIndex === i, one entry per op. */
export function groupOpsIntoSteps(
  ops: GraphOp[],
  opSteps: OpStepResult[]
): PlanStep[] {
  const steps: PlanStep[] = [];
  let i = 0;

  while (i < ops.length) {
    const op = ops[i];

    if (op.op === 'add_node') {
      const opIndices = [i];
      let j = i + 1;
      while (j < ops.length && refersToNode(ops[j], op.ref)) {
        opIndices.push(j);
        j++;
      }
      steps.push({
        primaryOpIndex: i,
        opIndices,
        nodeId: opSteps[i]?.primaryNodeId,
        ...changedIds(opIndices, opSteps),
      });
      i = j;
      continue;
    }

    if (op.op === 'add_branch') {
      const opIndices = [i];
      let j = i + 1;
      while (
        j < ops.length &&
        ops[j].op === 'add_condition' &&
        (ops[j] as AddConditionOp).conditional === op.ref
      ) {
        opIndices.push(j);
        j++;
      }
      steps.push({
        primaryOpIndex: i,
        opIndices,
        nodeId: opSteps[i]?.primaryNodeId,
        ...changedIds(opIndices, opSteps),
      });
      i = j;
      continue;
    }

    // delete_node, or a configure_node/rename_node/add_condition that
    // doesn't immediately follow its own add_node/add_branch (e.g. the
    // agent configures a node it didn't just add).
    const opIndices = [i];
    steps.push({
      primaryOpIndex: i,
      opIndices,
      nodeId: opSteps[i]?.primaryNodeId,
      ...changedIds(opIndices, opSteps),
    });
    i++;
  }

  return steps;
}

type PhaseKind = 'deletions' | 'skeleton' | 'branch-contents';

function phaseLabel(kind: PhaseKind): string {
  switch (kind) {
    case 'deletions':
      return 'Remove existing steps';
    case 'skeleton':
      return 'Core skeleton';
    case 'branch-contents':
      return 'Branch details';
  }
}

/** Groups checklist rows into 1-4 checkpoint phases:
 * - a leading run of delete_node steps is its own "deletions" phase
 * - the "core skeleton" phase runs through the first paths node's add_branch
 *   steps (all of them, before any of their contents)
 * - everything else is one "branch contents" phase, split further only past
 *   MAX_PHASE_STEPS rows
 * Degenerate plans (<=2 steps) collapse to a single phase -- trivial plans
 * shouldn't sprout empty or single-row checkpoint chips. */
export function chunkIntoPhases(
  steps: PlanStep[],
  ops: GraphOp[]
): PlanPhase[] {
  if (steps.length === 0) return [];
  if (steps.length <= 2) {
    return [{ label: 'Build workflow', steps }];
  }

  const buckets: { kind: PhaseKind; steps: PlanStep[] }[] = [];
  let cursor = 0;

  const deletionSteps: PlanStep[] = [];
  while (
    cursor < steps.length &&
    ops[steps[cursor].primaryOpIndex].op === 'delete_node'
  ) {
    deletionSteps.push(steps[cursor]);
    cursor++;
  }
  if (deletionSteps.length > 0) {
    buckets.push({ kind: 'deletions', steps: deletionSteps });
  }

  const skeletonSteps: PlanStep[] = [];
  let firstPathsNode: string | undefined;
  while (cursor < steps.length) {
    const op = ops[steps[cursor].primaryOpIndex];
    if (op.op === 'add_branch') {
      if (firstPathsNode === undefined) {
        firstPathsNode = op.pathsNode;
      }
      if (op.pathsNode !== firstPathsNode) break;
      skeletonSteps.push(steps[cursor]);
      cursor++;
      continue;
    }
    if (firstPathsNode !== undefined) break;
    skeletonSteps.push(steps[cursor]);
    cursor++;
  }
  if (skeletonSteps.length > 0) {
    buckets.push({ kind: 'skeleton', steps: skeletonSteps });
  }

  const remaining = steps.slice(cursor);
  for (let start = 0; start < remaining.length; start += MAX_PHASE_STEPS) {
    buckets.push({
      kind: 'branch-contents',
      steps: remaining.slice(start, start + MAX_PHASE_STEPS),
    });
  }

  if (buckets.length === 0) {
    return [{ label: 'Build workflow', steps }];
  }

  return buckets.map((bucket) => ({
    label: phaseLabel(bucket.kind),
    steps: bucket.steps,
  }));
}
